export type MetricSanityWarning = {
  metric: string;
  value: number;
  message: string;
};

const AVERAGE_RANK_FORMULA_NOTE =
  "Average rank must be recomputed as (sum(sumOfPosition) / sum(sumOfImpressions)) + 1 across rows; do not average daily avgOfRank values.";

export function buildAverageRankGuidanceNote(): string {
  return AVERAGE_RANK_FORMULA_NOTE;
}

export function detectImplausibleMetricWarnings(
  metrics: Record<string, number | null | undefined>,
): MetricSanityWarning[] {
  const warnings: MetricSanityWarning[] = [];

  for (const [metric, rawValue] of Object.entries(metrics)) {
    if (typeof rawValue !== "number" || !Number.isFinite(rawValue)) continue;

    if (
      (metric === "ctr" ||
        metric === "conversionRate" ||
        metric.endsWith("Rate")) &&
      (rawValue < 0 || rawValue > 1)
    ) {
      warnings.push({
        metric,
        value: rawValue,
        message: `${metric} is outside the expected 0..1 range; verify units before reporting.`,
      });
    }

    if (
      (metric === "avgOfRank" || metric === "avgRank") &&
      (rawValue < 1 || rawValue > 100)
    ) {
      warnings.push({
        metric,
        value: rawValue,
        message: `${metric} looks implausible for Search rank; confirm aggregation formula and date range.`,
      });
    }

    if (
      (metric === "totalClicks" ||
        metric === "totalImpressions" ||
        metric === "totalSessionCount") &&
      rawValue < 0
    ) {
      warnings.push({
        metric,
        value: rawValue,
        message: `${metric} is negative; totals should not be negative.`,
      });
    }
  }

  return warnings;
}

const SEARCH_ANALYTICS_AGGREGATION_GUIDANCE =
  "Read each row's tableType before comparing totals: SITE_IMPRESSION corresponds to searchdata_site_impression (property-level counting); URL_IMPRESSION corresponds to searchdata_url_impression (URL-level counting). Multiple URLs from one property in the same search result can count as multiple URL impressions but one property impression. URL-level impressions can exceed property-level impressions, even for a filtered URL group; totals need not match. This difference alone is not evidence of impossible or corrupt data and does not invalidate other breakdowns. Do not derive a subset share or reconcile totals across different tableType values. Use matching tableType, dates, search types, and filters for like-for-like comparisons; if counting grain is unknown, explain the limitation instead of assuming it.";

/**
 * Explain Search Console counting grain so tool consumers do not treat
 * property/URL impression differences as corrupt data.
 *
 * @returns The same request-independent guidance for tool descriptions and
 * result notes. Callers must inspect row tableType when applying it. This pure
 * helper takes no input, performs no I/O, and does not throw.
 */
export function buildSearchAnalyticsAggregationGuidanceNote(): string {
  return SEARCH_ANALYTICS_AGGREGATION_GUIDANCE;
}
